"use client";

import { useState } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Icon,
  Image,
  Switch,
  Text,
} from "@chakra-ui/react";
import { useLocale, useTranslations } from "next-intl";
import { LuLogIn, LuLogOut } from "react-icons/lu";
import { NextLink } from "@/_components/ui/NextLink";
import { authService } from "@/_services/userServices/auth";
import { useAuthStore } from "@/_stores/useAuthStore";
import { KnootLogo2026 } from "@/_images/svgs/chakra-icons/Logo";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { SignupDialog } from "./SignupDialog";
import { BsInboxesFill } from "react-icons/bs";
import vnFlag from "@/_images/png/vn.png";
import usFlag from "@/_images/png/us.png";

export function BenchmarkPageHeader() {
  const t = useTranslations("resume_benchmark");
  const locale = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { token, user } = useAuthStore();
  const [showSignup, setShowSignup] = useState(false);

  const displayName =
    [user?.userInfo.firstName, user?.userInfo.lastName]
      .filter(Boolean)
      .join(" ") ||
    user?.userInfo.email ||
    "";

  const router = useRouter();

  const handleLanguageChange = (newLocale: "vi" | "en") => {
    if (newLocale === locale) return;
    const pathWithoutLocale = pathname.replace(`/${locale}`, "") || "/";
    const query = searchParams.toString();
    router.push(`/${newLocale}${pathWithoutLocale}${query ? `?${query}` : ""}`);
  };

  const renderLanguageSwitch = () => {
    const isEnglish = locale === "en";

    return (
      <Switch.Root
        checked={isEnglish}
        onCheckedChange={(details) =>
          handleLanguageChange(details.checked ? "en" : "vi")
        }
        size="lg"
        colorPalette="dark"
        css={{
          "--switch-width": "3.4rem",
          "--switch-height": "2rem",
        }}
        aria-label={t("header.languageAria")}
        shadow="shadow-primary-xl"
        rounded="full"
      >
        <Switch.HiddenInput />
        <Switch.Control
          bg="dark.700"
          overflow="hidden"
          _checked={{ bg: "dark.700" }}
        >
          <Flex
            position="absolute"
            left="0.26rem"
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
            zIndex={0}
            filter="grayscale(100%)"
            opacity={isEnglish ? 0.55 : 0}
            transition="opacity 0.2s ease"
          >
            <Image
              src={vnFlag.src}
              alt=""
              boxSize={6}
              objectFit="contain"
              rounded="full"
              aria-hidden
            />
          </Flex>
          <Flex
            position="absolute"
            right="0.26rem"
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
            zIndex={0}
            filter="grayscale(100%)"
            opacity={isEnglish ? 0 : 0.55}
            transition="opacity 0.2s ease"
          >
            <Image
              src={usFlag.src}
              alt=""
              boxSize={6}
              objectFit="contain"
              rounded="full"
              aria-hidden
            />
          </Flex>
          <Switch.Thumb
            bg="white"
            boxShadow="sm"
            zIndex={1}
            overflow="hidden"
            display="flex"
            alignItems="center"
            justifyContent="center"
            _checked={{ bg: "white" }}
            scale="0.92"
          >
            <Switch.ThumbIndicator
              fallback={
                <Image
                  src={vnFlag.src}
                  alt={t("header.langViAlt")}
                  boxSize={8}
                  objectFit="contain"
                />
              }
            >
              <Image
                src={usFlag.src}
                alt={t("header.langEnAlt")}
                boxSize={8}
                objectFit="contain"
              />
            </Switch.ThumbIndicator>
          </Switch.Thumb>
        </Switch.Control>
      </Switch.Root>
    );
  };

  return (
    <>
      <Box
        position="sticky"
        top={0}
        zIndex={200}
        bg="rgba(30, 30, 37, 0.2)"
        backdropFilter="blur(20px)"
        borderBottom="1px solid"
        borderColor="whiteAlpha.100"
      >
        <Container maxW="1200px" px={{ base: 3, md: 8 }}>
          <Flex
            align="center"
            justify="space-between"
            py={{ base: 2.5, md: 3 }}
            gap={{ base: 2, md: 4 }}
            minH={{ base: "52px", md: "auto" }}
          >
            {/* Left: brand + home */}
            <HStack color="white" gap={{ base: 1, md: 4 }} minW={0} flex="1">
              <Flex
                gap={{ base: 2, md: 3 }}
                cursor="pointer"
                onClick={() => router.push(`/${locale}/resume-benchmark`)}
                alignItems="center"
                minW={0}
                flexShrink={1}
              >
                <KnootLogo2026 boxSize={{ base: 7, md: 8 }} flexShrink={0} />
                <Box minW={0}>
                  <Text
                    fontFamily="heading"
                    color="inherit"
                    fontSize={{ base: "sm", md: "md" }}
                    lineHeight="1.2"
                    truncate
                  >
                    {t("header.brand")}
                  </Text>
                  <Text
                    fontSize="xs"
                    color="whiteAlpha.600"
                    display={{ base: "none", md: "block" }}
                    lineHeight="1.2"
                  >
                    {t("header.product")}
                  </Text>
                </Box>
              </Flex>

              {token && (
                <NextLink
                  href={`/${locale}/resume-benchmark/home`}
                  ml={{ base: 0, md: 6 }}
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  px={{ base: 0, md: 3 }}
                  py={{ base: 0, md: 1.5 }}
                  minW={{ base: "40px", md: "auto" }}
                  minH={{ base: "40px", md: "auto" }}
                  rounded="lg"
                  fontSize="sm"
                  color="whiteAlpha.700"
                  flexShrink={0}
                  aria-label={t("header.home")}
                  transition="all 0.2s ease"
                  _hover={{
                    textDecoration: "none",
                    color: "white",
                    bg: "whiteAlpha.100",
                  }}
                >
                  <Flex gap={2} flexDir="row" alignItems="center">
                    <Icon as={BsInboxesFill} boxSize={4} color="inherit" />
                    <Text
                      fontSize="sm"
                      color="inherit"
                      display={{ base: "none", md: "block" }}
                    >
                      {t("header.home")}
                    </Text>
                  </Flex>
                </NextLink>
              )}
            </HStack>

            {/* Right: auth + language */}
            <HStack gap={{ base: 2, md: 4 }} flexShrink={0}>
              {token ? (
                <>
                  {displayName && (
                    <Text
                      fontSize="sm"
                      color="neutral.100"
                      fontWeight="medium"
                      display={{ base: "none", sm: "block" }}
                      maxW={{ base: "100px", md: "160px" }}
                      truncate
                    >
                      {displayName}
                    </Text>
                  )}
                  <Button
                    size="sm"
                    variant="subtle"
                    colorPalette="red"
                    borderRadius="lg"
                    fontSize="xs"
                    minW={{ base: "40px", md: "auto" }}
                    px={{ base: 2.5, md: 3 }}
                    onClick={() =>
                      authService.logout(`/${locale}/resume-benchmark`)
                    }
                    aria-label={t("header.signOutAria")}
                  >
                    <Icon as={LuLogOut} boxSize={4} />
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="subtle"
                  colorPalette="indigo"
                  borderRadius="lg"
                  fontSize="xs"
                  flexShrink={0}
                  onClick={() => setShowSignup(true)}
                  shadow="shadow-primary-xl"
                  px={{ base: 2.5, md: 3 }}
                  gap={1.5}
                  aria-label={t("header.signIn")}
                >
                  <Icon as={LuLogIn} boxSize={4} />
                  {t("header.signIn")}
                </Button>
              )}
              <Box
                id="language-switcher"
                transform={{ base: "scale(0.88)", md: "none" }}
                transformOrigin="center right"
              >
                {renderLanguageSwitch()}
              </Box>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <SignupDialog isOpen={showSignup} onClose={() => setShowSignup(false)} />
    </>
  );
}

export const benchmarkPageBgStyle = {
  background: `
    radial-gradient(ellipse 80% 60% at 20% 10%, rgba(99,102,241,0.15) 0%, transparent 60%),
    radial-gradient(ellipse 60% 50% at 80% 80%, rgba(139,92,246,0.12) 0%, transparent 60%),
    radial-gradient(ellipse 40% 40% at 60% 30%, rgba(59,130,246,0.08) 0%, transparent 60%),
    #090912
  `,
};
